import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Plus } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { fmtAestDate, fmtAud, todayAestISO } from "../lib/format";
import { CsvButton, PrintButton } from "../components/ReportButtons";

export default function Quotes() {
  const { user, meta } = useAuth();
  const [sp, setSp] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [f, setF] = useState({
    status: sp.get("status") || "All",
    segment: sp.get("segment") || "",
    state: sp.get("state") || "",
    rep_id: sp.get("rep_id") || "",
    date_from: "", date_to: "", q: "",
    at_risk: sp.get("at_risk") === "true",
  });
  const reload = useCallback(async () => {
    setLoading(true);
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(f)) {
      if (v === false || v === "") continue;
      p.set(k, String(v));
    }
    const { data } = await api.get(`/quotes?${p}`);
    setRows(data); setLoading(false);
    // keep URL in sync with at_risk flag (useful for deep-links from dashboard)
    const next = new URLSearchParams(sp);
    if (f.at_risk) next.set("at_risk", "true"); else next.delete("at_risk");
    setSp(next, { replace: true });
  }, [f, sp, setSp]);
  useEffect(() => { reload(); }, [reload]);
  useEffect(() => { api.get("/users").then(r => setUsers(r.data)).catch(() => {}); }, []);
  const today = todayAestISO();
  const totals = rows.reduce((a, r) => { a[r.status] = (a[r.status]||0) + Number(r.value_of_sale||0); return a; }, {});

  return (
    <div className="space-y-4" data-testid="quotes-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold">Quote Register</h1>
          <div className="text-sm text-gray-500 flex flex-wrap gap-3 mt-1">
            <span>{rows.length} quote{rows.length!==1?"s":""}</span>
            <span>· Open {fmtAud(totals.OPEN||0)}</span>
            <span>· Won {fmtAud(totals.WON||0)}</span>
            <span>· Lost {fmtAud(totals.LOST||0)}</span>
            {f.at_risk && <span className="pill pill-red ml-2">At-risk only</span>}
          </div>
        </div>
        <Link to="/quotes/new" className="cesco-btn-primary flex items-center gap-2"
              data-testid="btn-new-quote">
          <Plus size={16}/> New quote
        </Link>
      </div>
      <div className="flex justify-end gap-2 print-hide">
        <CsvButton path="/reports/quotes.csv"
                   params={{
                     status: f.status === "All" ? "" : f.status,
                     state: f.state, segment: f.segment,
                     created_by_id: f.rep_id,
                     from_date: f.date_from, to_date: f.date_to,
                     at_risk: f.at_risk ? "true" : "",
                   }}
                   testId="quotes-csv"/>
        <PrintButton testId="quotes-print"/>
      </div>

      <div className="cesco-card p-4 grid grid-cols-2 md:grid-cols-6 gap-3 print-hide">
        <input className="cesco-input col-span-2 md:col-span-2" placeholder="Quote # contains…"
               value={f.q} onChange={e=>setF({...f, q:e.target.value})} data-testid="quotes-filter-q"/>
        <select className="cesco-input" value={f.status} onChange={e=>setF({...f, status:e.target.value})}
                data-testid="quotes-filter-status" disabled={f.at_risk}>
          <option>All</option>
          <option value="DRAFTING">Drafting</option>
          <option value="OPEN">Open</option>
          <option value="WON">Won</option>
          <option value="LOST">Lost</option>
        </select>
        <select className="cesco-input" value={f.segment} onChange={e=>setF({...f, segment:e.target.value})}
                data-testid="quotes-filter-segment">
          <option value="">All segments</option><option value="D">Drill</option><option value="M">Mixer</option>
        </select>
        <select className="cesco-input" value={f.state} onChange={e=>setF({...f, state:e.target.value})}>
          <option value="">All states</option>
          {meta?.au_states.map(s => <option key={s.code} value={s.code}>{s.code}</option>)}
        </select>
        {user?.role === "admin" && (
          <select className="cesco-input" value={f.rep_id} onChange={e=>setF({...f, rep_id:e.target.value})}>
            <option value="">Any rep</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        )}
        <input className="cesco-input" type="date" value={f.date_from} onChange={e=>setF({...f, date_from:e.target.value})}/>
        <input className="cesco-input" type="date" value={f.date_to} onChange={e=>setF({...f, date_to:e.target.value})}/>
        <label className="flex items-center gap-2 text-sm col-span-2 md:col-span-6">
          <input type="checkbox" checked={f.at_risk}
                 onChange={e=>setF({...f, at_risk: e.target.checked, status: e.target.checked ? "OPEN" : f.status})}
                 data-testid="quotes-filter-at-risk"/>
          <span>Show only <b>at-risk</b> quotes (OPEN past their expected close date)</span>
        </label>
      </div>

      <div className="cesco-card overflow-x-auto print-region">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="text-left px-3 py-2">Quote #</th><th className="text-left px-3 py-2">Company</th>
              <th className="text-left px-3 py-2">Segment</th>
              <th className="text-left px-3 py-2">Short Description</th>
              <th className="text-left px-3 py-2">Value</th>
              <th className="text-left px-3 py-2">State</th><th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Rep</th>
              <th className="text-left px-3 py-2">Quote Date</th><th className="text-left px-3 py-2">Reminder Date</th><th className="text-left px-3 py-2">Expected Close</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={11} className="p-4"><div className="skeleton h-6 w-full"/></td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={11} className="p-6 text-center text-gray-500">No quotes match</td></tr>}
            {(() => {
              const drafts = rows.filter(q => q.status === "DRAFTING");
              const rest   = rows.filter(q => q.status !== "DRAFTING");
              const renderRow = (q) => {
                const flash = q.reminder_date === today && q.status === "OPEN";
                const isDraft = q.status === "DRAFTING";
                return (
                  <tr key={q.id}
                      className={`border-t ${isDraft ? "bg-amber-50 hover:bg-amber-100" : "hover:bg-gray-50"}`}
                      data-testid={isDraft ? "quote-row-draft" : "quote-row"}>
                    <td className="px-3 py-2"><Link to={`/quotes/${q.id}`} className="font-medium hover:underline">{q.quote_number}</Link></td>
                    <td className="px-3 py-2"><Link to={`/companies/${q.company_id}`} className="hover:underline">{q.company_name}</Link></td>
                    <td className="px-3 py-2">{q.product_segment === "M" ? "Mixer" : "Drill"} · {q.product_category}</td>
                    <td className="px-3 py-2 max-w-[12rem] truncate" title={q.item_description || ""}>{q.item_description || "-"}</td>
                    <td className="px-3 py-2">{fmtAud(q.value_of_sale)}</td>
                    <td className="px-3 py-2">{q.state}</td>
                    <td className="px-3 py-2">
                      <span className={`pill ${q.status === "WON" ? "pill-green" : q.status === "LOST" ? "pill-red" : q.status === "DRAFTING" ? "pill-amber" : "pill-blue"}`}>
                        {q.status === "DRAFTING" ? "Drafting" : q.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-700">{q.created_by_name || "-"}</td>
                    <td className="px-3 py-2">{fmtAestDate(q.date_of_quote)}</td>
                    <td className="px-3 py-2"><span className={flash ? "reminder-flash" : ""}>{fmtAestDate(q.reminder_date)}</span></td>
                    <td className="px-3 py-2">{q.date_expected_purchase ? fmtAestDate(q.date_expected_purchase) : "-"}</td>
                  </tr>
                );
              };
              return (
                <>
                  {drafts.length > 0 && (
                    <>
                      <tr className="bg-amber-100/70">
                        <td colSpan={11} className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-amber-900" data-testid="drafting-section-header">
                          Drafting · {drafts.length} quote{drafts.length === 1 ? "" : "s"} in progress
                        </td>
                      </tr>
                      {drafts.map(renderRow)}
                      {rest.length > 0 && (
                        <tr className="bg-gray-100">
                          <td colSpan={11} className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                            Submitted quotes
                          </td>
                        </tr>
                      )}
                    </>
                  )}
                  {rest.map(renderRow)}
                </>
              );
            })()}
          </tbody>
        </table>
      </div>
    </div>
  );
}
